/**
 * The Dashboard's XLSX export (Bill Val Report, docs/superpowers/plans/2026-10-28-bv-nta-reports.md §1):
 * the figures on screen for one month, one sheet per panel. Pure — the page
 * hands the rows to exportMultiSheetXLSX.
 */
import type { ExportSheet } from '../utils/exportUtils';
import { monthLabel } from './billMonths';
import { WORKFLOW_LABELS, WORKFLOW_ORDER, type BillWorkflowStatus } from './billWorkflow';
import type { MismatchDrivers } from './mismatchDrivers';
import {
  ENROLLED_SITE_STATUS, NEEDS_A_PERSON, sumClientTotals,
  type ClientTotals, type FlowStep, type Headline, type SiteCounts,
} from './dashboardMetrics';

export type DashboardExportInput = {
  /** `YYYY-MM`. */
  month: string;
  tolerance: number;
  head: Headline;
  /** Null when the site counts did not load. */
  sites: SiteCounts | null;
  flow: readonly FlowStep[];
  progress: Record<BillWorkflowStatus, number>;
  clients: readonly ClientTotals[];
  drivers: MismatchDrivers;
};

type SummaryRow = { Section: string; Metric: string; Value: string | number };

export function summaryRows(input: DashboardExportInput): SummaryRow[] {
  const { head, sites, flow, progress } = input;
  const total = sumClientTotals(input.clients);
  const row = (Section: string, Metric: string, Value: string | number): SummaryRow => ({ Section, Metric, Value });
  return [
    row('Report', 'Month (bills ending in)', monthLabel(input.month)),
    row('Report', 'Match within ±%', input.tolerance),
    row('Sites', 'Onboarded (BV sites)', sites ? sites.onboarded : 'Not loaded'),
    row('Sites', `Enrolled (status ${ENROLLED_SITE_STATUS})`, sites ? sites.enrolled : 'Not loaded'),
    row('Bills', 'Sites expected to bill', head.expected),
    row('Bills', 'Bills received', head.hasBill),
    row('Bills', 'No bill yet', head.missing),
    row('Bills', 'Validated', head.validated),
    row('Bills', 'Matched', head.matched),
    row('Bills', 'Mismatched', head.mismatched),
    row('Bills', 'Overcharged (bills)', head.overcharged),
    row('Bills', 'Overcharged ($)', total.overchargedDollars),
    row('Bills', 'Undercharged (bills)', head.undercharged),
    row('Bills', 'Undercharged ($)', total.underchargedDollars),
    row('Reports', 'Bills', head.bills),
    row('Reports', 'Sent', head.sent),
    row('Reports', 'Ready, not sent', head.notSent),
    row('Reports', 'Send failed', head.sendFailed),
    row('Reports', 'Changed since sent', head.changedSinceSent),
    row('Reports', 'Not validated yet', head.noReport),
    ...flow.map((s) => row('Invoice to validated', s.label, s.count)),
    ...WORKFLOW_ORDER.map((s) => row('Progress', WORKFLOW_LABELS[s], progress[s] ?? 0)),
  ];
}

const clientRow = (t: ClientTotals) => ({
  Client: t.entity || '(No client)',
  Bills: t.bills,
  Validated: t.validated,
  Overcharged: t.overcharged,
  'Overcharged $': t.overchargedDollars,
  Undercharged: t.undercharged,
  'Undercharged $': t.underchargedDollars,
  'Total difference $': t.differenceDollars,
  'Reports sent': t.sent,
});

/** One row per client, as the table sorts them, then the Total row. */
export function clientRows(clients: readonly ClientTotals[]): Record<string, unknown>[] {
  if (!clients.length) return [];
  return [...clients.map(clientRow), { ...clientRow(sumClientTotals(clients)), Client: 'Total' }];
}

export function needsAPersonRows(progress: Record<BillWorkflowStatus, number>, sendFailed: number): Record<string, unknown>[] {
  return [
    ...NEEDS_A_PERSON.map(({ status, label, hint }) => ({ Status: label, Bills: progress[status] ?? 0, 'Who / next': hint })),
    { Status: 'Send failed', Bills: sendFailed, 'Who / next': 'Resend from Bill Runs → Reports' },
  ];
}

/** The three driver views in one sheet; `View` says which. */
export function driverRows(d: MismatchDrivers): Record<string, unknown>[] {
  return [
    ...d.sections.map((s) => ({ View: 'Section', Name: s.label, Bills: s.bills, Over: s.over, Under: s.under })),
    ...d.retailers.map((r) => ({
      View: 'Retailer', Name: r.retailer || '(No retailer)', Bills: r.bills, Over: r.over, Under: r.under,
      Mismatched: r.mismatched, 'Mismatch rate %': r.rate,
    })),
    ...d.recurring.map((r) => ({
      View: 'Recurring', Name: `${r.label} · ${r.retailer}`, Bills: r.billIds.length, 'Per-bill amount $': r.amount,
    })),
  ];
}

export function dashboardSheets(input: DashboardExportInput): ExportSheet[] {
  return [
    { name: 'Summary', rows: summaryRows(input) },
    { name: 'By client', rows: clientRows(input.clients) },
    { name: 'Needs a person', rows: needsAPersonRows(input.progress, input.head.sendFailed) },
    { name: 'Mismatch drivers', rows: driverRows(input.drivers) },
  ];
}

export const dashboardFilename = (month: string) => `bill-validation-report-${month}`;
